import {defineStore, storeToRefs} from 'pinia';
import {computedAsync, useMemoize} from '@vueuse/core';
import {useLocaleStore} from './localeStore';
import {computed, watch, type Ref} from 'vue';
import {getPrimeVueLocale} from '../shared/primevue/locales/locale';
import {usePrimeVue} from 'primevue/config';
import type {ValidLocale} from '../api/models/global/locale';
import type {ValidTranslationKey, Translations} from '../shared/types/translations';
import {useDayjsState} from '@/shared/composables/useDayjsState';

function getStaticTranslations(locale: ValidLocale): Promise<Record<ValidTranslationKey, string>> {
    function importLocale(locale: string): Promise<{default: Partial<Translations>}> {
        switch (locale) {
            case 'de':
                return import('../../../assets/locales/de.json');
            case 'fr':
                return import('../../../assets/locales/fr.json');
            case 'es':
                return import('../../../assets/locales/es.json');
            case 'it':
                return import('../../../assets/locales/it.json');
            case 'en':
            default:
                return import('../../../assets/locales/en.json');
        }
    }

    return importLocale(locale).then(
        module => module.default as Record<ValidTranslationKey, string>
    );
}

export const useTranslationStore = defineStore(
    'translation',
    (): {translations: Ref<Translations>} => {
        const {currentLocale} = storeToRefs(useLocaleStore());

        const translations = computedAsync(
            () => getStaticTranslations(currentLocale.value),
            {} as Record<ValidTranslationKey, string>
        );

        usePrimeVueLocaleSwitcher(translations, currentLocale);

        return {translations};
    }
);

function usePrimeVueLocaleSwitcher(
    translations: Ref<Translations>,
    currentLocale: Ref<ValidLocale>
) {
    const primeVue = usePrimeVue();
    const localizedDayjs = useDayjsState();

    const getPrimeVueLocaleMemoized = useMemoize(
        (locale: ValidLocale, translations: Translations, _dayjsLocale: string) =>
            getPrimeVueLocale(locale, translations),
        {
            getKey(locale, translations, dayjsLocale) {
                return locale + '-' + dayjsLocale + '-' + Object.keys(translations).length;
            },
        }
    );

    const dayjsLocale = computed(() => localizedDayjs.reactiveDayjs.value.locale());

    watch(
        [translations, dayjsLocale],
        async ([translations, dayjsLocale]) => {
            // Reload PrimeVue locale when translations or dayjs locale changes
            // Dayjs locale is needed for generating month names
            primeVue.config.locale = await getPrimeVueLocaleMemoized(
                currentLocale.value,
                translations,
                dayjsLocale
            );
        },
        {immediate: true}
    );
}
